import { z } from 'zod';
import { MARKETS, type MarketDef } from './markets.js';
import type { Brand } from './types.js';
import { DEFAULT_DEPARTMENTS, type Department } from './access.js';

/**
 * WHITE-LABEL
 * -----------
 * Every firm using the platform is a tenant: its own brand, domains, markets,
 * plans, traders, accounts and staff. CK Funded Pro is the first tenant.
 * Nothing is shared between tenants except the code.
 */
export interface TenantBranding {
  /** Product name shown in the portals, e.g. "CK Funded Pro". */
  name: string;
  /** Short mark shown in the logo tile when there is no logo, e.g. "CK". */
  mark: string;
  logoUrl: string | null;
  primary: string;
  primaryInk: string;
  /** Page background and text colours for the client portal. */
  paper: string;
  ink: string;
  supportEmail: string;
  /** Optional video behind the sign-in screens (MP4/WebM the firm owns). The animated chart shows when empty. */
  loginVideoUrl?: string | null;
}

/** How clients pay for challenges until a payment gateway is connected. */
export interface PaymentMethod {
  id: string;
  label: string;
  /** Shown to the client at checkout, e.g. "Lipa Namba 123456, name CK FUNDED PRO". */
  instructions: string;
  enabled: boolean;
  /** How the money moves. Card needs a card processor; crypto shows the address and a QR code. */
  kind?: 'mobile_money' | 'bank' | 'card' | 'crypto' | 'local' | 'other';
  /** Countries that see this method. Empty means everyone. */
  countries?: string[];
  /** Lower shows first. */
  priority?: number;
  /** Shown instead when this one is down or full for the month. */
  backupId?: string | null;
  /** Spread volume across merchant accounts: once this month's confirmed payments reach the cap, the backup takes over. */
  monthlyCapUsd?: number | null;
  /** Finance can mark a method down; clients are routed to its backup straight away. */
  status?: 'up' | 'down';
  /** Crypto: the network, e.g. Base, Arbitrum, Solana, Ethereum, Tron. */
  network?: string | null;
  /** Crypto: the deposit address shown with a QR code. */
  address?: string | null;
}

/** Card and dispute rules for the firm. */
export interface PaymentRules {
  threeDs: 'off' | 'all' | 'high_risk';
  highRiskCountries: string[];
  blockPrepaidCards: boolean;
  blockedBins: string[];
  blockedCardCountries: string[];
  /** A chargeback freezes the client and holds their payouts. */
  chargebackAutoFreeze: boolean;
  /** Refund the challenge fee when the account's first reward is paid. */
  refundFeeOnFirstPayout: boolean;
}
export const DEFAULT_PAYMENT_RULES: PaymentRules = { threeDs: 'high_risk', highRiskCountries: [], blockPrepaidCards: true, blockedBins: [], blockedCardCountries: [], chargebackAutoFreeze: true, refundFeeOnFirstPayout: false };

/**
 * Payment methods for one client: their country's methods first, then by priority.
 * A method that is down or has reached its monthly cap is replaced by its backup (failover).
 */
export function routeMethods(methods: PaymentMethod[], country: string, usedThisMonthUsd: Record<string, number> = {}) {
  const usable = (m: PaymentMethod) => m.enabled && m.status !== 'down' && !(m.monthlyCapUsd && (usedThisMonthUsd[m.id] ?? 0) >= m.monthlyCapUsd);
  const forCountry = (m: PaymentMethod) => !m.countries?.length || m.countries.some((c) => c.toLowerCase() === country.toLowerCase());
  const out: (PaymentMethod & { replaces?: string })[] = [];
  for (const m of methods.filter((x) => x.enabled && forCountry(x))) {
    if (usable(m)) { if (!out.some((o) => o.id === m.id)) out.push(m); continue; }
    let b = methods.find((x) => x.id === m.backupId);
    const seen = new Set([m.id]);
    while (b && !usable(b) && !seen.has(b.id)) { seen.add(b.id); b = methods.find((x) => x.id === b!.backupId); }
    if (b && usable(b) && !out.some((o) => o.id === b!.id)) out.push({ ...b, replaces: m.label });
  }
  const local = (m: PaymentMethod) => (m.countries?.length ? 0 : 1);
  return out.sort((a, b) => local(a) - local(b) || (a.priority ?? 50) - (b.priority ?? 50));
}

export interface TenantSettings {
  paymentMethods: PaymentMethod[];
  paymentRules?: PaymentRules;
  /** Default revenue share for new affiliates, % of each paid order. */
  partnerCommissionPct: number;
  /** Smallest affiliate payout, USD. */
  partnerMinPayoutUsd: number;
  /** Wording on certificates issued to traders. */
  certificate: CertificateTemplate;
  security: SecuritySettings;
  /** When on, the client and partner portals show a maintenance page. The CRM keeps working. */
  maintenance: { on: boolean; message: string };
  /** Compliance: per-country rules that hide markets or portal features. */
  countryRules: CountryRule[];
  /** Departments and what each can do in the CRM. */
  departments: Department[];
  /** New features already handed to their default departments, so an owner's later removal sticks. */
  grantedFeatures?: string[];
}

export interface CountryRule {
  id: string;
  /** Country names as clients pick them at sign-up, e.g. "Kenya". */
  countries: string[];
  /** Markets clients from these countries cannot see or buy. */
  hideMarkets: string[];
  /** Client portal pages hidden for these countries, e.g. "copy", "affiliate", "leaderboard". */
  hideFeatures: string[];
  note: string;
}

export interface CertificateTemplate {
  heading: string;
  signatory: string;
  signatoryTitle: string;
  footer: string;
}

export interface SecuritySettings {
  /** Every staff member must use an authenticator code. */
  require2fa: boolean;
  /** If not empty, the CRM only accepts staff from these IP addresses. */
  ipAllowlist: string[];
  /** Staff are signed out after this many idle minutes. */
  sessionMinutes: number;
}

export interface Tenant {
  id: string;
  legalName: string;
  branding: TenantBranding;
  /** Host names that belong to this tenant, e.g. client.ckfundedpro.com, crm.ckfundedpro.com. */
  domains: string[];
  /** Markets this tenant offers, with its own names and status. */
  markets: MarketDef[];
  settings: TenantSettings;
  createdAt: string;
}

export const DEFAULT_SETTINGS: TenantSettings = {
  paymentMethods: [
    { id: 'mpesa', label: 'M-Pesa', instructions: '[Lipa Namba / till number and account name]', enabled: true },
    { id: 'airtel', label: 'Airtel Money', instructions: '[Airtel Money number and account name]', enabled: true },
    { id: 'bank', label: 'Bank transfer', instructions: '[Bank, account name, account number, SWIFT]', enabled: true },
    { id: 'usdt', label: 'USDT (TRC20)', instructions: '[USDT TRC20 wallet address]', enabled: true },
  ],
  partnerCommissionPct: 10,
  partnerMinPayoutUsd: 150,
  certificate: { heading: 'Certificate of achievement', signatory: 'Collins Kessy', signatoryTitle: 'Founder & CEO', footer: 'Simulated evaluation. Rewards are performance-based and follow programme terms.' },
  security: { require2fa: false, ipAllowlist: [], sessionMinutes: 60 },
  maintenance: { on: false, message: 'We are upgrading the platform and will be back shortly.' },
  countryRules: [],
  departments: DEFAULT_DEPARTMENTS.map((d) => ({ ...d, permissions: [...d.permissions], builtIn: true })),
};

/** Keeps edited and custom departments, and adds any built-in department added since. */
/** Features added after a firm was set up go to the departments that get them by default, once. */
export const NEW_FEATURE_DEFAULTS: Record<string, string[]> = { 'support.chat': ['support', 'retention', 'cx_qa'], 'clients.ban': ['risk', 'compliance', 'fraud'], 'hr.suite': ['hr'], 'hr.payroll': ['hr', 'finance'] };
function grantNewFeatures(st: TenantSettings) {
  const done = new Set(st.grantedFeatures ?? []);
  for (const [feat, depts] of Object.entries(NEW_FEATURE_DEFAULTS)) {
    if (done.has(feat)) continue;
    for (const d of st.departments) if (depts.includes(d.id) && !d.permissions.includes(feat)) d.permissions = [...d.permissions, feat];
    done.add(feat);
  }
  st.grantedFeatures = [...done];
}

function mergeDepartments(saved: Department[] | undefined): Department[] {
  const out = [...(saved ?? [])];
  for (const d of DEFAULT_DEPARTMENTS) if (!out.some((x) => x.id === d.id)) out.push({ ...d, permissions: [...d.permissions], builtIn: true });
  // Owner and Admin always have every feature, including ones added later.
  for (const d of out) if (d.id === 'owner' || d.id === 'admin') d.permissions = [...DEFAULT_DEPARTMENTS[0]!.permissions];
  return out;
}

/** Markets and pages a client may use, after country rules and any staff restriction on the client. */
export function clientAccess(t: Tenant, country: string, blockedMarkets: string[] = []) {
  const rules = (t.settings.countryRules ?? []).filter((r) => r.countries.some((c) => c.toLowerCase() === country.toLowerCase()));
  const hiddenMarkets = new Set([...blockedMarkets, ...rules.flatMap((r) => r.hideMarkets), ...t.markets.filter((m) => m.status === 'hidden').map((m) => m.brand)]);
  return { hiddenMarkets: [...hiddenMarkets], hiddenFeatures: [...new Set(rules.flatMap((r) => r.hideFeatures))] };
}

/** Fills settings added after a tenant was created, and moves CK to its current brand colours. */
export function normalizeTenant(t: Tenant): Tenant {
  const d = structuredClone(DEFAULT_SETTINGS);
  t.settings = { ...d, ...t.settings, certificate: { ...d.certificate, ...t.settings?.certificate }, security: { ...d.security, ...t.settings?.security }, maintenance: { ...d.maintenance, ...t.settings?.maintenance }, countryRules: t.settings?.countryRules ?? [], departments: mergeDepartments(t.settings?.departments) };
  grantNewFeatures(t.settings);
  // October 2026: CK launches with CK Funded only; the other markets show as coming soon until switched on.
  if (t.id === 'ck' && !(t.settings.grantedFeatures ?? []).includes('m:ckfunded-only')) {
    for (const m of t.markets) if (m.brand !== 'prop' && m.status === 'open') m.status = 'coming_soon';
    t.settings.grantedFeatures = [...(t.settings.grantedFeatures ?? []), 'm:ckfunded-only'];
  }
  t.settings.paymentRules = { ...DEFAULT_PAYMENT_RULES, ...t.settings.paymentRules };
  if (t.id === 'ck' && t.branding.primary === '#F2B705' && t.branding.paper === '#F6EEDC') Object.assign(t.branding, CK_BRANDING);
  // Earlier market colours were all browns; move untouched ones to the current set.
  const OLD = ['#F2B705', '#6B4423', '#0D0C0A', '#B07A45', '#2B1D10'];
  for (const m of t.markets) {
    const def = MARKETS.find((x) => x.brand === m.brand);
    if (def && OLD.includes(m.accent)) { m.accent = def.accent; m.ink = def.ink; }
  }
  return t;
}

const CK_BRANDING: TenantBranding = {
  name: 'CK Funded Pro', mark: 'CK', logoUrl: null,
  primary: '#FEAB41', primaryInk: '#000000', paper: '#F4F4F2', ink: '#141414',
  supportEmail: 'support@ckfundedpro.com', loginVideoUrl: null,
};

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a hex colour like #F2B705');

export const TenantInputSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]{2,32}$/, 'Use 2–32 lowercase letters, numbers and dashes'),
  legalName: z.string().min(2).max(120),
  branding: z.object({
    name: z.string().min(2).max(60),
    mark: z.string().min(1).max(3),
    logoUrl: z.string().url().nullable().default(null),
    primary: hex, primaryInk: hex, paper: hex, ink: hex,
    supportEmail: z.string().email(),
    loginVideoUrl: z.string().url().nullable().optional(),
  }),
  domains: z.array(z.string().regex(/^[a-z0-9.-]+$/i)).min(1).max(10),
  /** Which of the five market types the firm offers. Defaults to all three challenge markets. */
  brands: z.array(z.enum(['prop', 'futures', 'perps', 'predict', 'fx'])).min(1).default(['prop', 'futures', 'perps']),
  /** The firm's first CRM owner. */
  owner: z.object({ email: z.string().email(), name: z.string().min(2).max(80), password: z.string().min(10) }),
});

export type TenantInput = z.input<typeof TenantInputSchema>;

export function makeTenant(input: TenantInput, now = new Date()): Tenant {
  const t = TenantInputSchema.parse(input);
  const brandSet = new Set<Brand>(t.brands);
  return {
    id: t.id,
    legalName: t.legalName,
    branding: t.branding,
    domains: t.domains.map((d) => d.toLowerCase()),
    markets: MARKETS.filter((m) => brandSet.has(m.brand)).map((m) => ({ ...m })),
    settings: structuredClone(DEFAULT_SETTINGS),
    createdAt: now.toISOString(),
  };
}

/** CK Funded Pro, the home tenant. */
export function ckTenant(): Tenant {
  return {
    id: 'ck',
    legalName: 'CK Funded Pro Limited',
    branding: { ...CK_BRANDING },
    domains: ['client.ckfundedpro.com', 'crm.ckfundedpro.com', 'partners.ckfundedpro.com', 'ckfundedpro.com', 'www.ckfundedpro.com'],
    markets: MARKETS.map((m) => ({ ...m })),
    settings: structuredClone(DEFAULT_SETTINGS),
    createdAt: new Date(0).toISOString(),
  };
}

/** What the portals may see about a tenant. */
export const publicTenant = (t: Tenant) => ({
  id: t.id, legalName: t.legalName, branding: t.branding, markets: t.markets.filter((m) => m.status !== 'hidden'),
  paymentMethods: t.settings.paymentMethods.filter((m) => m.enabled).map(({ id, label, instructions, enabled, kind, network, address }) => ({ id, label, instructions, enabled, kind, network, address })),
  maintenance: t.settings.maintenance?.on ? t.settings.maintenance : null,
  certificate: t.settings.certificate,
});
